/**
 * usePayPal Hook
 *
 * Encapsulates PayPal Checkout initialization and button rendering
 * via Braintree SDK.
 *
 * @example
 * const { paypalReady, paypalInstance, renderPayPalButton } = usePayPal({
 *   braintreeClient: window.braintreeClient,
 *   braintreeLoading: false,
 * });
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import paypalCheckout from 'braintree-web/paypal-checkout';
import logger from '../utils/logger';

/**
 * Custom hook for PayPal integration via Braintree
 *
 * @param {Object} options
 * @param {boolean} options.braintreeLoading - Whether Braintree is still loading
 * @returns {Object} PayPal state and methods
 */
export const usePayPal = ({ braintreeLoading }) => {
  const [paypalInstance, setPaypalInstance] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);

  // Store button instances for proper cleanup
  const buttonInstances = useRef({});

  /**
   * Initialize PayPal Checkout instance after Braintree client is ready
   */
  useEffect(() => {
    const initPayPal = async () => {
      // Requires braintree client to be initialized first
      if (!window.braintreeClient || braintreeLoading || paypalInstance) return;

      try {
        // Create PayPal Checkout instance using Braintree client
        const paypalCheckoutInstance = await paypalCheckout.create({
          client: window.braintreeClient,
        });

        // Load PayPal SDK with configuration
        await paypalCheckoutInstance.loadPayPalSDK({
          currency: 'USD',
          intent: 'capture',
        });

        setPaypalInstance(paypalCheckoutInstance);
        setPaypalReady(true);
        logger.debug('✅ PayPal initialized successfully');

      } catch (err) {
        logger.error('Error initializing PayPal:', err);
        logger.error('PayPal Error Details:', {
          code: err.code,
          message: err.message,
          type: err.type,
          details: err.details
        });
        // Don't show error to user - PayPal is optional, cards still work
      }
    };

    if (!braintreeLoading) {
      initPayPal();
    }
  }, [braintreeLoading, paypalInstance]);

  /**
   * Cleanup a PayPal button instance
   * @param {string} containerId - The container ID to cleanup
   */
  const cleanupPayPalButton = useCallback(async (containerId) => {
    const existingInstance = buttonInstances.current[containerId];
    if (existingInstance) {
      try {
        await existingInstance.close();
        logger.debug(`🧹 PayPal button cleaned up: ${containerId}`);
      } catch (err) {
        // Ignore errors during cleanup - button might already be closed
        logger.debug(`PayPal cleanup (ignored): ${err.message}`);
      }
      delete buttonInstances.current[containerId];
    }
  }, []);

  /**
   * Render PayPal button in a DOM container
   *
   * @param {Object} options
   * @param {string} options.containerId - DOM element ID to render button in
   * @param {boolean} options.isExpressCheckout - Whether this is express checkout flow
   * @param {boolean} options.isSubmitButton - Whether this is the main submit button (Shopify style)
   * @param {Function} options.calculateTotal - Function that returns total amount
   * @param {Function} options.onValidate - Validation callback, return false to prevent popup
   * @param {Function} options.onApprove - Called with (nonce, details) on successful approval
   * @param {Function} options.onError - Called on error
   * @param {Function} options.onCancel - Called when user cancels
   */
  const renderPayPalButton = useCallback(async ({
    containerId,
    isExpressCheckout = false,
    isSubmitButton = false,
    calculateTotal,
    onValidate,
    onApprove,
    onError,
    onCancel,
  }) => {
    if (!paypalInstance || !window.paypal) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    // IMPORTANT: Close existing button instance BEFORE clearing DOM
    // This prevents "Detected container element removed from DOM" error
    await cleanupPayPalButton(containerId);

    // Now safe to clear the container
    container.innerHTML = '';

    // Different styles for Express Checkout vs Submit Button
    const buttonStyle = isSubmitButton
      ? {
          // Shopify-style: blue, rect shape, "Pay with PayPal"
          layout: 'horizontal',
          color: 'blue',
          shape: 'rect',
          label: 'pay',
          height: 55,
          tagline: false,
        }
      : {
          // Express Checkout: gold, rect, smaller
          layout: 'horizontal',
          color: 'gold',
          shape: 'rect',
          label: 'paypal',
          height: 40,
          tagline: false,
        };

    // Create button instance
    const buttonInstance = window.paypal.Buttons({
      fundingSource: window.paypal.FUNDING.PAYPAL,

      style: buttonStyle,

      // Validate form before opening PayPal popup
      onClick: (data, actions) => {
        if (!isExpressCheckout && onValidate) {
          if (!onValidate()) {
            return actions.reject();
          }
        }
        return actions.resolve();
      },

      createOrder: () => {
        const total = calculateTotal ? calculateTotal() : '0.00';

        return paypalInstance.createPayment({
          flow: 'checkout',
          amount: total,
          currency: 'USD',
          intent: 'capture',
          enableShippingAddress: isExpressCheckout,
          shippingAddressEditable: false,
        });
      },

      onApprove: async (data) => {
        try {
          // Tokenize PayPal payment
          const payload = await paypalInstance.tokenizePayment(data);
          logger.debug('✅ PayPal tokenized:', payload);

          // Call the approval handler with nonce and details
          if (onApprove) {
            await onApprove(payload.nonce, payload.details, isExpressCheckout);
          }
        } catch (err) {
          logger.error('PayPal tokenization error:', err);
          if (onError) {
            onError(err);
          }
        }
      },

      onCancel: () => {
        logger.debug('PayPal payment cancelled');
        if (onCancel) {
          onCancel();
        }
      },

      onError: (err) => {
        logger.error('PayPal error:', err);
        if (onError) {
          onError(err);
        }
      },

    });

    // Store instance for future cleanup
    buttonInstances.current[containerId] = buttonInstance;

    // Render the button
    await buttonInstance.render(`#${containerId}`);
  }, [paypalInstance, cleanupPayPalButton]);

  return {
    paypalInstance,
    paypalReady,
    renderPayPalButton,
    cleanupPayPalButton,
  };
};

export default usePayPal;
